/**
 * WordPress Dependencies
 */
import { RichText, useBlockProps } from '@wordpress/block-editor';
import { RawHTML } from '@wordpress/element';

/**
 * External Dependencies
 */
import classNames from 'classnames';

/**
 * Internal Dependencies
 */
import ICONS from '../../controls/icon-picker-control/icons';

// Helper function to get prefix based on type
const getPrefix = (index, type) => {
    switch (type) {
        case 'numeric':
            return `${index + 1}.`;
        case 'question':
            return `Q${index + 1}.`;
        case 'alpha-upper':
            return `${String.fromCharCode(65 + (index % 26))}.`;
        case 'alpha-lower':
            return `${String.fromCharCode(97 + (index % 26))}.`;
        case 'roman-upper':
            return `${toRoman(index + 1)}.`;
        case 'roman-lower':
            return `${toRoman(index + 1).toLowerCase()}.`;
        case 'bracket-numeric':
            return `[${index + 1}]`;
        case 'bracket-question':
            return `[Q${index + 1}]`;
        default:
            return '';
    }
};

// Helper function to convert to Roman numerals
const toRoman = num => {
    if (num > 50) return num.toString(); // Fallback for large numbers

    const romanNumerals = [
        { value: 50, numeral: 'L' },
        { value: 40, numeral: 'XL' },
        { value: 10, numeral: 'X' },
        { value: 9, numeral: 'IX' },
        { value: 5, numeral: 'V' },
        { value: 4, numeral: 'IV' },
        { value: 1, numeral: 'I' }
    ];

    let result = '';
    for (const { value, numeral } of romanNumerals) {
        while (num >= value) {
            result += numeral;
            num -= value;
        }
    }
    return result;
};

const Save = ({ attributes }) => {
    const {
        uniqueId,
        items,
        iconPosition,
        enablePrefix,
        titleTag,
        sourceType,
        customIcon,
        icon,
        showIcon,
        // Auto numbering attributes
        enableAutoNumbering = false,
        numberingType = 'numeric',
        numberingPosition = 'before'
    } = attributes;

    const blockProps = useBlockProps.save({
        className: classNames(uniqueId, 'wp-block-faqmate-faq', {
            'has-auto-numbering': enableAutoNumbering
        }),
        'data-numbering-type': enableAutoNumbering ? numberingType : undefined
    });

    return (
        <div {...blockProps}>
            <div className="asfa__container">
                {items &&
                    items?.length > 0 &&
                    items.map((item, index) => {
                        const prefix = enableAutoNumbering ? getPrefix(index, numberingType) : '';

                        return (
                            <div className="single-faq" key={index} data-index={index}>
                                <div
                                    className={classNames('asfa__head', {
                                        asfa__head_reverse: iconPosition
                                    })}
                                    role="button"
                                    aria-expanded="false"
                                    tabIndex={0}
                                >
                                    <div
                                        className={classNames('asfa__heading_txt', {
                                            'has-prefix': enablePrefix,
                                            'has-numbering': enableAutoNumbering,
                                            [`numbering-${numberingPosition}`]: enableAutoNumbering
                                        })}
                                    >
                                        {enableAutoNumbering && numberingPosition === 'before' && (
                                            <span className="asfa__numbering">{prefix}</span>
                                        )}
                                        <RichText.Content tagName={titleTag} className="asfa__heading_tag" value={item?.question} />
                                        {enableAutoNumbering && numberingPosition === 'after' && (
                                            <span className="asfa__numbering">{prefix}</span>
                                        )}
                                    </div>
                                    {showIcon !== false && (
                                        <div className="asfa__icon">
                                            <div className="faqmate__icon">
                                                {sourceType === 'library' ? ICONS[icon] : <RawHTML>{customIcon}</RawHTML>}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="asfa__body" style={{ display: 'none' }}>
                                    <div
                                        className={classNames('asfa__body_inner', {
                                            'has-prefix': enablePrefix
                                        })}
                                    >
                                        <RichText.Content tagName="div" className="answer" value={item?.answer} />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
            </div>
        </div>
    );
};

export default Save;
